import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AuthService } from '../../../service/auth.service';
import { YukkApi } from '../../../service/yukkapi.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { take, takeUntil, tap } from 'rxjs/operators';
import { marked } from 'marked';
import { RoutingService } from '../../../service/routing.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ChatMessage,
  AIMessage,
  ChatAIMessage,
  ChatDetailsResponse,
} from '../interfaces/chart-details.interface';

import { combineLatest, Observable, Subject } from 'rxjs';
import {
  MatBottomSheet,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';

import { PortfolioSetupComponent } from '../../settings/portfolio-setup/portfolio-setup.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { UserAccount } from 'src/models/user.types';
import {
  BreakpointService,
  ScreenSize,
} from 'src/app/shared/services/breakpoints.service';
import { ChatParentStateService } from '../services/chat-parent-state.service';
import { MobilePortfolioSetupComponent } from '../../mobile/mobile-portfolio-setup/mobile-portfolio-setup.component';
import { PortfolioSetupService } from '../../settings/portfolio-setup/portfolio-setup-service';
import { ChatNewsletterEntityService } from '../services/chat-entitiy.service';
import { ChatNewsletterPromptService } from '../services/chat-newsletter-prompt.service';
import { newsletter_prompts } from '../services/chat-newsletter-prompts';
import { ChatStreamService } from '../services/chat-stream.service';
import { ChatDataProcessorService } from '../services/chat-data-processor.service';
import { ChatLoadMessageService } from '../services/chat-load-message.service';
import { ChatApiService } from 'src/app/shared/services/customer-api/chat-api.service';

declare global {
  interface Window {
    pendo?: {
      showGuideById: (guideId: string) => void;
      // Add other pendo methods you use here
    };
  }
}

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  headerText = 'YUKKA Lab News LLM';

  chatInput: string; // question from user
  chatOutput = []; // answer from LLM stored in an array
  chatHistory = []; // storing chatInput (called message) and answer (called history) in an array
  loadingAnswer = false;
  loading = false;
  error = false;
  params: { chatId?: string; [key: string]: unknown };
  chatDetails: (ChatMessage | ChatAIMessage)[];
  previousChatId: string | null = null;
  chatLoadMessage: string = '';

  // properties for history sent in auth.llmChat
  chatIdQuestion = '';
  chatIdAnswer: string | null = '';
  chatIdHistory: [string, string][] = [];
  chatTitle = '';
  portfolioCompanies: string[] = [];

  isDialogOpen: boolean = false;

  userAccountInfo: UserAccount;
  portfolioSetUpProcess$: Observable<boolean>;
  screenSize$ = this.breakpointService.screenSize$;
  summaryEntitiy: string | null = null;
  newsletterPromptId: string | null = null;
  public dialogState = {
    isOpen: false,
    dialogRef: null as MatDialogRef<PortfolioSetupComponent> | null,
    bottomSheetRef:
      null as MatBottomSheetRef<MobilePortfolioSetupComponent> | null,
  };

  pendoDesktop = {
    guideId: 'jtLs23Y6lFwKnr4mJkZq56EIHFI',
    stepId: 'zMbrZy2vOq8TWBnAmJNMeN3pOvE',
  };
  pendoMobile = {
    guideId: 'CIpmfHuL1sWXJW-zB1aVE4YT5fE',
    stepId: 'AR_iheYHCBdFHt6ntonW2ohn2EA',
  };

  constructor(
    public auth: AuthService,
    public yukkApi: YukkApi,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    public routing: RoutingService,
    private route: ActivatedRoute,
    private router: Router,
    private breakpointService: BreakpointService,
    private chatParentStateService: ChatParentStateService,
    private chatNewsletterEntityService: ChatNewsletterEntityService,
    private chatNewsletterPromptService: ChatNewsletterPromptService,
    private bottomSheet: MatBottomSheet,
    public portfolioSetupService: PortfolioSetupService,
    private chatStreamService: ChatStreamService,
    private chatDataProcessor: ChatDataProcessorService,
    private chatLoadMessageService: ChatLoadMessageService,
    private chatApi: ChatApiService,
  ) {
    this.portfolioSetUpProcess$ = this.portfolioSetupService.setupProcess$;
  }

  ngOnInit(): void {
    combineLatest([
      this.route.queryParams,
      this.auth.accountInfo$,
      this.chatNewsletterEntityService.entitiyName$,
      this.chatNewsletterPromptService.promptId$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, accountInfo, entitiyName, promptId]) => {
        this.userAccountInfo = accountInfo;
        this.summaryEntitiy = entitiyName;
        this.newsletterPromptId = promptId || null;
        this.handleQueryParams(params);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeExistingDialogs();
  }

  private handleQueryParams(params: {
    chatId?: string;
    context?: string;
    newsletter_entity?: string;
  }): void {
    this.params = params;
    const currentChatId = this.params.chatId;
    const isNewChat = currentChatId !== this.previousChatId;

    if (isNewChat) {
      this.resetChatDetails();

      if (currentChatId) {
        this.loading = true;
        if (!this.summaryEntitiy && !this.newsletterPromptId) {
          this.loadConversation(currentChatId);
        }
      }
    }

    this.previousChatId = currentChatId;

    if (params.context === 'llm_signup' && !this.isDialogOpen) {
      this.handleSignupDialog();
    }

    if (this.summaryEntitiy && currentChatId) {
      this.generateEntitiyPrompt(this.summaryEntitiy);
    }

    if (this.newsletterPromptId && currentChatId) {
      this.sendNewsletterPrompt(this.newsletterPromptId);
    }
  }

  private resetChatDetails(): void {
    this.chatStreamService.stopCurrentStream();
    this.loadingAnswer = false;
    this.chatDetails = [];
    this.chatIdHistory = [];
    this.chatTitle = '';
    this.chatParentStateService.setParentChatId(null);
    this.chatParentStateService.setParentChatTitle(null);
  }

  private handleSignupDialog(): void {
    if (this.dialogState.isOpen) return;

    this.dialogState.isOpen = true;
    this.closeExistingDialogs();

    this.breakpointService.screenSize$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        tap((screen: ScreenSize) => {
          if (screen.isMobile) {
            this.openMobileDialog();
          } else {
            this.openDesktopDialog();
          }
        }),
      )
      .subscribe();
  }

  private closeExistingDialogs(): void {
    this.dialogState.dialogRef?.close();
    this.dialogState.bottomSheetRef?.dismiss();
  }

  private openMobileDialog(): void {
    this.dialogState.bottomSheetRef = this.bottomSheet.open(
      MobilePortfolioSetupComponent,
      {
        disableClose: false,
        autoFocus: false,
        panelClass: 'mobile-portfolio-setup',
      },
    );

    this.dialogState.bottomSheetRef
      .afterDismissed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.cleanupDialogState();
        this.portfolioSetUpAfterClosed(this.pendoMobile);
      });
  }

  private openDesktopDialog(): void {
    this.dialogState.dialogRef = this.dialog.open(PortfolioSetupComponent);

    this.dialogState.dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.cleanupDialogState();
        this.portfolioSetUpAfterClosed(this.pendoDesktop);
      });
  }

  private portfolioSetUpAfterClosed(pendoParams: {
    guideId: string;
    stepId: string;
  }): void {
    if (this.portfolioSetupService.isSetupComplete()) {
      this.handleModalNavigation(pendoParams);
      return;
    }
    this.portfolioSetupService
      .defaultPortfolio()
      .pipe(
        takeUntil(this.destroy$),
        tap({
          next: () => {
            this.portfolioSetupService.setSetUpComplete(true);
            this.handleModalNavigation(pendoParams);
          },
          error: (error) => {
            console.error('Portfolio setup failed:', error);
            this.handleError();
          },
        }),
      )
      .subscribe();
  }

  private handleModalNavigation(pendoParams: {
    guideId: string;
    stepId: string;
  }): void {
    const guideId = pendoParams.guideId;
    const stepId = pendoParams.stepId;
    const queryParams: {
      context: string | null;
      pendo?: string;
      step?: string;
    } = {
      context: null,
    };

    if (!this.params.pendo) {
      queryParams.pendo = guideId;
      queryParams.step = stepId;
    }

    this.router
      .navigate([], {
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then(() => {
        if (window.pendo && typeof window.pendo.showGuideById === 'function') {
          window.pendo.showGuideById(guideId);
        } else {
          console.warn('Pendo not initialized or unavailable');
        }
      });
  }

  private cleanupDialogState(): void {
    this.dialogState.isOpen = false;
    this.dialogState.dialogRef = null;
    this.dialogState.bottomSheetRef = null;
  }

  private generateEntitiyPrompt(entitiyName: string): string {
    if (!entitiyName || entitiyName.length === 0) {
      return '';
    }
    this.chatInput = `What is the latest news about ${entitiyName}?`;
    this.postTheMessage(this.chatInput);
  }

  private sendNewsletterPrompt(promptId: string): void {
    const prompt = newsletter_prompts[promptId];
    if (!prompt) {
      console.warn(`No prompt found for newsletter_prompt_id: "${promptId}"`);
      return;
    }
    this.chatNewsletterPromptService.setPromptId('');
    this.chatInput = prompt;
    this.postTheMessage(prompt);
  }

  private loadConversation(chatId: string) {
    this.chatIdHistory = [];
    this.chatApi.getChatDetails(chatId).subscribe(
      (chatDetails) => {
        const result = this.chatDataProcessor.processChatDetails(
          chatDetails as ChatDetailsResponse,
          chatId,
          this.chatIdHistory,
          (title: string, id: string) => {
            this.chatTitle = title;
            this.chatParentStateService.setParentChatId(id);
            this.chatParentStateService.setParentChatTitle(title);
          },
        );
        this.chatDetails = result.processedMessages;
        this.chatIdHistory = result.updatedHistory;
        this.loading = false;
      },
      (error) => {
        console.error('Error fetching chat details:', error);
      },
    );
  }

  // post request into chatId conversation
  private postChatConversation(
    chatId: string,
    actor: string,
    message: string,
  ): void {
    this.chatApi.postChatConversation(chatId, actor, message).subscribe({
      next: () => {
        this.loadConversation(chatId);
      },
      error: (err) => console.error('Error posting message:', err),
    });
  }
  private updateUIForNewMessage(message: string): void {
    this.chatInput = '';
    if (!this.error) {
      this.chatHistory.push([marked.parse(message), '']);
      this.chatOutput.push({});
    } else {
      this.chatHistory[this.chatHistory.length - 1][0] = message;
    }
    this.error = false;
    setTimeout(() => {
      const target = document.getElementById('llmContainer');
      if (target && typeof target.scrollTo === 'function') {
        target.scrollTo(0, target.scrollHeight + 40);
      }
    }, 500);
  }

  private postTheMessage(userQuestion: string): void {
    const message = userQuestion.trim();
    const chatId = this.params.chatId;

    this.chatStreamService.stopCurrentStream();
    this.updateUIForNewMessage(message);
    this.loadingAnswer = true;
    this.chatLoadMessageService.setLoadingMessage('');
    this.postChatConversation(chatId, 'human', message);

    const chatPayload = {
      message,
      history: JSON.parse(JSON.stringify(this.chatIdHistory)),
      ...this.chatStreamService.generateUserInfo(this.userAccountInfo),
    };

    this.chatStreamService.startStream(
      chatPayload,
      (streamData) => {
        if (streamData.type === 'json') {
          this.handleStreamedJsonData(
            streamData.data as Record<string, unknown>,
          );
        } else if (streamData.type === 'text') {
          this.chatLoadMessageService.setLoadingMessage(
            streamData.data as string,
          );
        }
      },
      (error) => {
        console.error('Stream error:', error);
        this.handleError();
      },
      () => {
        console.log('Stream completed');
        this.loadingAnswer = false;
      },
    );
  }
  private processResponse(
    chatResponse: AIMessage,
    entityName: string[] | null,
    chatId: string,
  ) {
    if (!chatResponse.facts || chatResponse.facts.length === 0) {
      this.chatOutput[this.chatOutput.length - 1] = Object.assign(
        {},
        chatResponse,
        {
          entityName,
        },
      );
    } else {
      this.chatOutput[this.chatOutput.length - 1] = Object.assign(
        {},
        chatResponse,
        {
          entityName,
        },
      );
    }

    // Scroll down a little bit more after receiving the final answer if there's no error
    if (!this.error) {
      setTimeout(() => {
        const target = document.getElementById('llmContainer');
        if (target && typeof target.scrollTo === 'function') {
          target.scrollTo(0, target.scrollHeight + 80);
        }
      }, 0);
    }

    this.loadingAnswer = false;

    if (chatId) {
      const stringifiedAiMessage = JSON.stringify(
        this.chatOutput[this.chatOutput.length - 1],
      );
      this.postChatConversation(chatId, 'ai', stringifiedAiMessage);
    }
  }

  public stopStreaming() {
    this.chatStreamService.stopCurrentStream();
    this.loadingAnswer = false;
    this.chatLoadMessageService.setLoadingMessage('');
  }

  submitRelatedQuestions(question) {
    if (question && typeof question === 'string' && !this.loading) {
      this.postTheMessage(question);
    }
  }
  onSubmit() {
    if (
      this.chatInput &&
      typeof this.chatInput === 'string' &&
      this.chatInput.trim() &&
      !this.loading
    ) {
      this.postTheMessage(this.chatInput);
    }
  }

  handleError() {
    // Clean up any active subscriptions on error
    this.chatStreamService.stopCurrentStream();

    this.error = true;
    this.loading = false;
    this.loadingAnswer = false;
    this.snackBar.open('Something went wrong. Please try again later.', 'OK', {
      duration: 10000,
    });
  }

  private handleStreamedJsonData(data: Record<string, unknown>): void {
    const aiMessage = data as unknown as AIMessage;
    this.chatHistory[this.chatHistory.length - 1][1] = marked.parse(
      aiMessage.history[aiMessage.history.length - 1][1],
    );
    this.chatDataProcessor.processLLMResponse(aiMessage).subscribe(
      ({ response: chatResponse, entityName }) => {
        this.processResponse(chatResponse, entityName, this.params.chatId);
      },
      () => {
        this.handleError();
      },
    );
  }
}
